'use client'

import type { RenderLink } from '@ui/web/utils'
import { Button } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import { getStaticAssetUrl } from '@helper/utils/file'
import cn from '@config/tailwind/cn'

import { ContactAdminButton } from '../../contact-admin-button'

interface EmptyProps {
  type: 'web' | 'school'
  renderCreateLink?: RenderLink
  renderExploreLink?: RenderLink
  /** Member is not assigned to any class — they manage nobody, so there is no roster to build. */
  noClass?: boolean
  /** Organization has no students at all — prompt to register rather than to fill a class. */
  noStudents?: boolean
  /** Name of the class the roster is filtered to, used in the "no students in this class" copy. */
  schoolClassName?: string | null
  /** Class the "Kelola rombel" CTA should open. */
  teamId?: string | null
}

/**
 * The three states a roster reaches before it has rows, mirroring the reading-journal and
 * lesson-trend empties so every dashboard tab tells the same story in the same order:
 * no class → no students anywhere → no students in the chosen class.
 */
export function Empty({
  type,
  renderCreateLink,
  renderExploreLink,
  noClass,
  noStudents,
  schoolClassName,
  teamId,
}: EmptyProps) {
  const createStudentButton = renderCreateLink ? (
    <Button asChild>{renderCreateLink({ children: `Daftarkan ${type === 'web' ? 'anak' : 'murid'}` })}</Button>
  ) : null

  const manageClassButton = renderCreateLink ? (
    <Button asChild>{renderCreateLink({ children: 'Kelola kelas', meta: teamId ? { teamId } : undefined })}</Button>
  ) : null

  return (
    <section className='srd:text-neutral-black srd:my-10 srd:flex srd:h-fit srd:w-full srd:flex-col srd:items-center srd:justify-center srd:gap-y-4 srd:text-center srd:min-h-[50dvh]'>
      {(() => {
        // State 1 — member is not assigned to any class. Nobody to list, and no CTA they can act on.
        if (noClass) {
          return (
            <>
              <h6 className={cn(typographyVariants({ variant: 'title-lg' }))}>
                Kamu belum ditugaskan ke rombel mana pun
              </h6>

              <p className={cn(typographyVariants({ variant: 'body-lg' }))}>
                Hubungi admin sekolah untuk mendapatkan akses ke kelas dan mulai memantau siswamu.
              </p>

              {/*
                Makes the sentence above actionable instead of an instruction the teacher has to
                carry out somewhere else. School only: on the family app the reader IS the owner of
                their organization, so there is no admin to ask — and `StudentsTab` derives this
                branch from the active organization without a family guard, so the check has to
                happen here. The button gates itself on role and class count as well.
              */}
              {type === 'school' && <ContactAdminButton className='srd:mt-2 srd:w-fit' />}

              {renderExploreLink && (
                <Button asChild variant='outline'>
                  {renderExploreLink({ children: 'Jelajahi beranda' })}
                </Button>
              )}
            </>
          )
        }

        // State 2 — the organization has no students at all. Same copy as the Kelola murid empty,
        // which is the page this CTA hands them to.
        if (noStudents) {
          return (
            <>
              <h6 className={cn(typographyVariants({ variant: 'title-lg' }))}>
                Mulai daftarkan {type === 'web' ? 'anak-anakmu' : 'murid-muridmu'}
              </h6>

              <img src={getStaticAssetUrl('illustration/alien.png')} alt='' width={70} height={70} draggable={false} />

              <p className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:max-w-[640px]')}>
                Dengan akun masing-masing, {type === 'web' ? 'anak' : 'murid'} bisa membaca dan mengerjakan tugas dari
                rumah atau sekolah. Kamu akan melihat perkembangan dan pertumbuhan mereka setiap saat secara otomatis.
              </p>

              {createStudentButton}
            </>
          )
        }

        // State 3 — the chosen class has no students in it yet.
        return (
          <>
            <h6 className={cn(typographyVariants({ variant: 'title-lg' }))}>
              Belum ada murid di dalam {schoolClassName ?? 'kelas ini'}
            </h6>

            <p className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:max-w-[640px]')}>
              Tambahkan murid ke dalam kelas ini agar guru kelas dapat mulai mengajar.
            </p>

            {manageClassButton}
          </>
        )
      })()}
    </section>
  )
}
